import React, { useState, useEffect } from "react";
import { scrollToSection } from "../utils/scrollToSection";
import {
  AppBar,
  Toolbar,
  CssBaseline,
  Typography,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse,
} from "@mui/material";
import { styled } from "@mui/system";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { motion } from "framer-motion";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useLocation, useNavigate, Link } from "react-router-dom";
import logo from "../assets/imperial-drummers-logo-white.png";
import { getAllServices } from "../data/servicesData";

// Styled components
const NavLinks = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(10),
  display: "flex",
  [theme.breakpoints.down("md")]: {
    display: "none", // Hide nav links on mobile
  },
}));

const Logo = styled(Typography)(({ theme }) => ({
  cursor: "pointer",
  [theme.breakpoints.up("md")]: {
    flexGrow: 1,
  },
}));

const StyledLink = styled("a")(({ theme }) => ({
  textDecoration: "none",
  textAlign: "center",
  color: "white",
  fontSize: "18px",
  marginLeft: theme.spacing(4),
  cursor: "pointer",
  padding: "8px 16px",
  borderRadius: "8px",
  transition: "all 0.3s ease",
  fontWeight: 500,
  letterSpacing: "0.5px",
  "&:hover": {
    color: "#ffd700",
    backgroundColor: "rgba(255, 215, 0, 0.1)",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 15px rgba(255, 215, 0, 0.2)",
  },
}));

function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [servicesExpanded, setServicesExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const isServicesPage = location.pathname.startsWith('/services');

  const [activeSection, setActiveSection] = useState(null);

  // Track which homepage section is in view
  useEffect(() => {
    if (!isHomePage) {
      setActiveSection(null);
      return;
    }

    const sectionIds = ['about-section', 'portfolio-section', 'luxury-section', 'booking-form'];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: 0 }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [isHomePage, location.pathname]);

  // Clear active section when at top of page
  useEffect(() => {
    if (!isHomePage) return;
    const handleScroll = () => {
      if (window.scrollY < 200) setActiveSection(null);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  const isActiveNav = (sectionId, link) => {
    if (link) return location.pathname.startsWith(link);
    if (!isHomePage) return false;
    if (sectionId === 'top') return !activeSection && !isServicesPage;
    return activeSection === sectionId;
  };

  const activeStyle = {
    color: '#ffd700',
    borderBottom: '2px solid #ffd700',
    borderRadius: '8px 8px 0 0',
  };

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  // Handle nav link clicks - scroll on homepage, navigate on other pages
  const handleNavClick = (sectionId) => {
    // Booking form exists on every page, always scroll
    if (sectionId === 'booking-form') {
      scrollToSection(sectionId);
      return;
    }
    // On homepage, scroll to the section
    if (isHomePage) {
      if (sectionId === 'top') {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        scrollToSection(sectionId);
      }
    } else {
      // On other pages, navigate to homepage and scroll to section
      navigate('/', { state: { scrollTo: sectionId } });
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: 1300,
          backgroundColor: "rgba(10, 0, 21, 0.7)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 4px 30px rgba(0, 0, 0, 0.3)",
          transition: 'background-color 0.3s ease'
        }}
      >
        <CssBaseline />
        <Toolbar sx={{
          display: 'flex',
          justifyContent: isMobile ? 'center' : 'space-between',
          position: 'relative',
          maxWidth: 'lg',
          width: '100%',
          mx: 'auto',
          px: { md: 4, lg: 6 }
        }}>
          <Logo
            variant="h4"
            onClick={() => {
              if (isHomePage) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                navigate('/', { state: { scrollTo: 'top' } });
              }
            }}
            sx={{ textDecoration: 'none' }}
          >
            <img src={logo} alt="Imperial Drummers Logo" loading="lazy" style={{ paddingTop: "10px", paddingBottom: "10px", width: "150px", height: "auto" }} />
          </Logo>

          {/* Show Menu Icon on Mobile */}
          {isMobile ? (
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer(!drawerOpen)}
              sx={{
                position: 'absolute',
                right: 16,
                transition: "all 0.3s ease",
                "&:hover": {
                  color: "#ffd700",
                  backgroundColor: "rgba(255, 215, 0, 0.1)"
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <NavLinks>
              <StyledLink onClick={() => handleNavClick('top')} sx={isActiveNav('top') ? activeStyle : {}}>Home</StyledLink>

              {/* Services with dropdown */}
              <Box
                sx={{
                  position: 'relative',
                  '&:hover .services-dropdown': {
                    opacity: 1,
                    visibility: 'visible',
                    transform: 'translateY(0)',
                  }
                }}
              >
                <StyledLink
                  onClick={() => navigate('/services')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    ...(isActiveNav(null, '/services') ? activeStyle : {})
                  }}
                >
                  Services
                  <ExpandMoreIcon sx={{ fontSize: 18, transition: 'transform 0.3s' }} />
                </StyledLink>
                <Box
                  className="services-dropdown"
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    mt: 0.5,
                    minWidth: 240,
                    backgroundColor: 'rgba(10, 0, 21, 0.95)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: 2,
                    border: '1px solid rgba(255, 215, 0, 0.15)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    opacity: 0,
                    visibility: 'hidden',
                    transform: 'translateY(-8px)',
                    transition: 'all 0.25s ease',
                    py: 1,
                    zIndex: 1400,
                  }}
                >
                  {getAllServices().map((service) => (
                    <Box
                      key={service.slug}
                      component={Link}
                      to={`/services/${service.slug}`}
                      sx={{
                        display: 'block',
                        px: 2.5,
                        py: 1.5,
                        color: 'white',
                        textDecoration: 'none',
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        letterSpacing: '0.3px',
                        transition: 'all 0.2s ease',
                        backgroundColor: location.pathname === `/services/${service.slug}`
                          ? 'rgba(255, 215, 0, 0.12)'
                          : 'transparent',
                        '&:hover': {
                          color: '#ffd700',
                          backgroundColor: 'rgba(255, 215, 0, 0.1)',
                          pl: 3,
                        }
                      }}
                    >
                      {service.title}
                    </Box>
                  ))}
                </Box>
              </Box>

              <StyledLink onClick={() => handleNavClick('about-section')} sx={isActiveNav('about-section') ? activeStyle : {}}>About</StyledLink>
              <StyledLink onClick={() => handleNavClick('portfolio-section')} sx={isActiveNav('portfolio-section') ? activeStyle : {}}>Portfolio</StyledLink>
              <StyledLink onClick={() => handleNavClick('luxury-section')} sx={isActiveNav('luxury-section') ? activeStyle : {}}>Luxury</StyledLink>
              {/* <StyledLink onClick={() => handleNavClick('testimonials-section')} sx={isActiveNav('testimonials-section') ? activeStyle : {}}>Reviews</StyledLink> */}
              <StyledLink onClick={() => handleNavClick('booking-form')} sx={isActiveNav('booking-form') ? activeStyle : {}}>Contact</StyledLink>
            </NavLinks>
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer Component for Mobile */}
      <Drawer
        anchor="top"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(5, 0, 15, 0.98)',
            backdropFilter: 'blur(30px) saturate(150%)',
            color: 'white',
            paddingTop: '100px',
            minHeight: '100vh',
            backgroundImage: 'radial-gradient(circle at 50% -20%, rgba(255, 215, 0, 0.05) 0%, transparent 70%)',
            boxShadow: '0 -10px 50px rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          px: 2
        }}>
          <List sx={{ width: '100%', maxWidth: '500px', pt: 0, pb: 0 }}>
            {[
              { label: "Home", sectionId: "top" },
              { label: "About", sectionId: "about-section" },
              { label: "Services", link: "/services", hasChildren: true },
              { label: "Portfolio", sectionId: "portfolio-section" },
              { label: "Luxury", sectionId: "luxury-section" },
              // { label: "Reviews", sectionId: "testimonials-section" },
              { label: "Contact", sectionId: "booking-form" }
            ].map((item, index) => {
              const isActive = isActiveNav(item.sectionId, item.link);
              return (
                <React.Fragment key={item.label}>
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                  >
                    <ListItem
                      button
                      onClick={() => {
                        if (item.hasChildren) {
                          setServicesExpanded(!servicesExpanded);
                        } else {
                          setDrawerOpen(false);
                          if (item.sectionId) handleNavClick(item.sectionId);
                        }
                      }}
                      sx={{
                        textAlign: "center",
                        mb: 1,
                        borderRadius: 2,
                        mx: 'auto',
                        width: '100%',
                        maxWidth: '300px',
                        py: 1.5,
                        transition: 'all 0.3s ease',
                        borderBottom: isActive ? '1px solid rgba(255, 215, 0, 0.3)' : '1px solid rgba(255, 215, 0, 0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 215, 0, 0.05)',
                          transform: 'translateX(5px)',
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            {item.label}
                            {item.hasChildren && (
                              <ExpandMoreIcon sx={{
                                fontSize: 20,
                                transition: 'transform 0.3s',
                                transform: servicesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                color: isActive ? '#ffd700' : 'rgba(255,255,255,0.5)'
                              }} />
                            )}
                          </Box>
                        }
                        primaryTypographyProps={{
                          sx: {
                            fontFamily: '"EB Garamond SC", serif',
                            fontSize: '1.4rem',
                            fontWeight: 500,
                            letterSpacing: '2px',
                            color: isActive ? '#ffd700' : 'white',
                            textTransform: 'uppercase',
                            textAlign: 'center',
                          }
                        }}
                      />
                    </ListItem>
                  </motion.div>

                  {/* Service sub-items for mobile */}
                  {item.hasChildren && (
                    <Collapse in={servicesExpanded} timeout="auto" unmountOnExit>
                      <List disablePadding sx={{ mb: 1 }}>
                        {getAllServices().map((service) => (
                          <ListItem
                            button
                            key={service.slug}
                            component={Link}
                            to={`/services/${service.slug}`}
                            onClick={() => {
                              setDrawerOpen(false);
                              setServicesExpanded(false);
                            }}
                            sx={{
                              py: 1.2,
                              px: 4,
                              mx: 'auto',
                              maxWidth: '400px',
                              borderRadius: 2,
                              mb: 0.5,
                              justifyContent: 'center',
                              backgroundColor: location.pathname === `/services/${service.slug}`
                                ? 'rgba(255, 215, 0, 0.12)'
                                : 'transparent',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 215, 0, 0.08)',
                              }
                            }}
                          >
                            <ListItemText
                              primary={service.title}
                              primaryTypographyProps={{
                                sx: {
                                  fontFamily: '"EB Garamond SC", serif',
                                  fontSize: '1rem',
                                  fontWeight: 500,
                                  letterSpacing: '1px',
                                  color: location.pathname === `/services/${service.slug}` ? '#ffd700' : 'rgba(255,255,255,0.85)',
                                  textAlign: 'center',
                                }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>

          {/* Decorative element */}
          <Box sx={{
            mt: 4,
            width: '80px',
            height: '3px',
            backgroundColor: '#ffd700',
            borderRadius: 1,
            boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
            mx: 'auto'
          }} />

          {/* Tagline */}
          <Typography
            variant="body2"
            sx={{
              mt: 3,
              fontFamily: '"EB Garamond SC", serif',
              color: 'rgba(255, 215, 0, 0.9)',
              textAlign: 'center',
              fontSize: '0.95rem',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              width: '100%',
              px: 2,
              fontWeight: 500,
              textShadow: '0 2px 10px rgba(255, 215, 0, 0.3)'
            }}
          >
            UK's Premier Dhol Entertainment
          </Typography>
        </Box>
      </Drawer>
    </>
  );
}

export default Navbar;